/* eslint no-control-regex:0 */
import locale from '@glu/locale';
import validationProcessor from './validationProcessor';

export type TValidatorFn<D = any, P = any> = (key: keyof D, params: P, data: D) => boolean | string;

export interface IValidators<D = any, P = any> {
  // Global validation functions
  functions: Record<string, TValidatorFn<D, P>> & {
    alphaNumeric(key: keyof D, enabled: boolean, data: D): boolean;

    /**
     * this method validates that an array is not empty
     * except in the case where the parameter evaluates to false
     */
    arrayNotEmpty(key: keyof D, enabled: boolean, data: D): boolean;

    emailAddress(key: keyof D, enabled: boolean, data: D): boolean;

    isNumeralsOnly(key: keyof D, enabled: boolean, data: D): boolean;

    isNumeric(key: keyof D, enabled: boolean, data: D): boolean;

    maxLength(key: keyof D, maxLength: number | boolean, data: D): boolean;

    /**
     * Ensures the value is less than or equal to the max value supplied as a parameter.
     */
    maxValue(key: keyof D, maxValue: number | boolean, data: D): boolean;

    /**
     * Ensures the value is less than the max value supplied as a parameter.
     * This version excludes the max value as valid so that the validation will
     * not pass if the value === the max value.
     */
    maxValueExclusive(key: keyof D, maxValue: number | boolean, data: D): boolean;
    minLength(key: keyof D, minLength: number, data: D): boolean;

    /**
     * Ensures the value is greater than or equal to the min value supplied as a parameter.
     */
    minValue(key: keyof D, minValue: boolean | number, data: D): boolean;
    /**
     * Ensures the value is greater than the min value supplied as a parameter.
     * This version excludes the min value as valid so that the validation will
     * not pass if the value === the min value.
     */
    minValueExclusive(key: keyof D, minValue: number | boolean, data: D): boolean;

    /**
     * Validates that the given value is present and not only whitespace
     */
    required(key: keyof D, isRequired: boolean, data: D): boolean | string;

    us10DigitPhone(key: keyof D, enabled: boolean, data: D): boolean;

    usZipCode(key: keyof D, enabled: boolean, data: D): boolean;
  };

  /**
   * Messages are defined in this hash and must EXACTLY match the name of the validator
   * function for which they are supplying a message.
   *
   * The placeholders, such as "{0}" are replaced with:
   *        0   - The name of the field that is being validated
   *        1-N - The parameters passed in the param field of the validator as specified
   *              in the validator spec property. Note that to get more than one parameter
   *              in you should use an array to ensure an exact order.
   */
  initMessages(): {
    // messages, must match the function names for Validators.functions EXACTLY
    alphaNumeric: string;
    arrayNotEmpty: string;
    emailAddress: string;
    isNumeralsOnly: string;
    isNumeric: string;
    maxLength: string;
    maxValue: string;
    maxValueExclusive: string;
    minLength: string;
    minValue: string;
    minValueExclusive: string;
    required: string;
    us10DigitPhone: string;
    usZipCode: string;
  };

  /**
   * messages are to be set by the implementing subclass after all locale bundles have been loaded.
   * If you have no custom messages just set the result of initMessages above
   */
  messages: Record<string, string>;

  patterns: Record<string, RegExp> & {
    alphaNumeric: RegExp;
    date: RegExp;
    // obtained from http://thedersen.com/projects/backbone-validation version 0.9.1
    emailAddress: RegExp;
    numeralsOnly: RegExp;
    // Matches 1234567890, (123)456-7890, 123-456-7890, 123.456.7890, but not 12-3456-7890, etc.
    us10DigitPhone: RegExp;
    // Matches 12345 or 12345-6789 (US zip code)
    usZip: RegExp;
  };
}

const isNullOrUndefined = (value: unknown) => value === null || typeof value === 'undefined';

const patterns = {
  alphaNumeric: /^[a-zA-Z0-9]*$/,

  date: /^(1[0-2]|0?[1-9])\/((0[1-9])|([12][0-9])|(3[01]))\/([12][7890]\d{2})$/,

  // taken from zod: https://github.com/colinhacks/zod/blob/master/src/types.ts#L567
  emailAddress: /^(?!\.)(?!.*\.\.)([A-Z0-9_+-.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i,

  numeralsOnly: /^\d*$/,

  // Matches 1234567890, (123)456-7890, 123-456-7890, 123.456.7890, but not 12-3456-7890, etc.
  us10DigitPhone: /^(?:\(\d{3}\)\s?|\d{3}[.\s-]?)\d{3}[\s.-]?\d{4}$|^\d*$/,

  // Matches 12345 or 12345-6789 (US zip code)
  usZip: /^\d{5}(?:-\d{4})?$/,
};

// Determines whether or not a value is empty
function hasValue(value: unknown) {
  return !(
    isNullOrUndefined(value) ||
    (typeof value === 'string' && value.replace(/\s/gm, '').length < 1) ||
    (Array.isArray(value) && !value.length)
  );
}

const validators: IValidators = {
  // Global validation functions
  functions: {
    alphaNumeric(key, enabled, data) {
      if (!enabled) {
        return true;
      }

      return patterns.alphaNumeric.test(data[key]);
    },

    arrayNotEmpty(key, enabled, data) {
      if (!enabled) {
        return true;
      }

      const value = data[key];

      if (!value || !value.length) {
        return false;
      }

      return value.length > 1 || (value[0] !== null && value[0] !== undefined);
    },

    emailAddress(key, enabled, data) {
      if (!enabled) {
        return true;
      }

      return !data[key] || patterns.emailAddress.test(data[key]);
    },

    isNumeralsOnly(key, enabled, data) {
      if (!enabled) {
        return true;
      }

      const value = data[key];
      return !value || patterns.numeralsOnly.test(value);
    },

    isNumeric(key, enabled, data) {
      let value = data[key];

      if (!enabled || !value || typeof value === 'number') {
        return true;
      }

      if (typeof value === 'string') {
        value = value.trim();

        /* to enable numbers with commas and other local stuff beyond strict numbers we
         * use use numeral (glu/number) to create a number that is not negative and
         * check that it worked */
        // eslint-disable-next-line no-restricted-globals
        return !value || (!isNaN(Number(value)) && Number(value) >= 0);
      }

      return false;
    },

    maxLength(key, maxLength, data) {
      if (maxLength === false) {
        return true;
      }

      let value = data[key];

      if (typeof value === 'number') {
        value = `${value}`;
      }

      return !value || value.length <= maxLength;
    },

    maxValue(key, maxValue, data) {
      if (maxValue === false) {
        return true;
      }

      const value = parseFloat(data[key]);

      return (!value && value !== 0) || value <= (maxValue as number);
    },

    maxValueExclusive(key, maxValue, data) {
      if (maxValue === false) {
        return true;
      }

      const value = parseFloat(data[key]);

      return (!value && value !== 0) || value < (maxValue as number);
    },

    minLength(key, minLength, data) {
      let value = data[key];

      if (typeof value === 'number') {
        value = `${value}`;
      }

      return !value || value.length >= minLength;
    },

    minValue(key, minValue, data) {
      if (minValue === false) {
        return true;
      }

      const value = parseFloat(data[key]);

      return (!value && value !== 0) || value >= (minValue as number);
    },

    minValueExclusive(key, minValue, data) {
      if (minValue === false) {
        return true;
      }

      const value = parseFloat(data[key]);

      return (!value && value !== 0) || value > (minValue as number);
    },

    required(key, isRequired, data) {
      const value = data[key];
      const valueNotEmpty = hasValue(value);

      if (isRequired) {
        return valueNotEmpty;
      }

      if (!isRequired && !valueNotEmpty) {
        /* if its not required and it has no value return the special ignore all validators
         * value to disable all other validators after this one */
        return validationProcessor.IGNORE_ALL_OTHER_VALIDATORS;
      }

      return true;
    },

    us10DigitPhone(key, enabled, data) {
      if (!enabled || !data[key]) {
        return true;
      }

      return patterns.us10DigitPhone.test(data[key]) || !data[key];
    },

    usZipCode(key, enabled, data) {
      if (!enabled || !data[key]) {
        return true;
      }

      return patterns.usZip.test(data[key]);
    },
  },

  initMessages: function initBuiltInMessages() {
    return {
      // messages, must match the function names for Validators.functions EXACTLY
      alphaNumeric: locale.get('validator.alphaNumeric'),
      arrayNotEmpty: locale.get('validator.arrayNotEmpty'),
      emailAddress: locale.get('validator.emailAddress'),
      isNumeralsOnly: locale.get('validator.isNumeralsOnly'),
      isNumeric: locale.get('validator.isNumeric'),
      maxLength: locale.get('validator.maxLength'),
      maxValue: locale.get('validator.maxValue'),
      maxValueExclusive: locale.get('validator.maxValueExclusive'),
      minLength: locale.get('validator.minLength'),
      minValue: locale.get('validator.minValue'),
      minValueExclusive: locale.get('validator.minValueExclusive'),
      required: locale.get('validator.required'),
      us10DigitPhone: locale.get('validator.us10DigitPhone'),
      usZipCode: locale.get('validator.usZipCode'),
    };
  },

  /* messages are to be set by the implementing subclass after all locale bundles have been loaded.
   * If you have no custom messages just set the result of initMessages above */
  messages: {},

  patterns,
};

export default validators;
